import { reaction } from 'mobx';
import globalStore from '@src/mobx-store/root-store/global-store';
import { thirdPartyAuthStore, xoyoAuthStore } from '@src/mobx-store/root-store/auth';
import ActivitySessionStore from '@src/mobx-store/root-store/activity-session-store';
import TeamFactStore from '@src/mobx-store/root-store/team-fact-store';
import { loadTeamContext } from '@src/services/team';
import { postTeamShareContext } from '@src/utils/miniprogram-share-bridge';

const activitySessionStore = new ActivitySessionStore();
const teamFactStore = new TeamFactStore();

// 小程序转发分享上下文：全页面统一"当前账号人视角"的唯一推送点。
// 两路用户级数据（get_config 的个人分享码、页面登记的队伍事实）任一变化即重推；
// 消息在壳内积压、转发时取最后一条，因此任何时刻转发卡片都跟随最新事实。
// 无登录 owner（登出/账号切换中）不推送，避免旧账号队伍串入新会话。
reaction(
  () => ({
    owner: activitySessionStore.contextOwnerAccount,
    hasTeam: teamFactStore.hasTeam,
    teamId: teamFactStore.teamId,
    shareCode: activitySessionStore.personalShareCode
  }),
  context => {
    if (!context.owner) {
      return;
    }
    postTeamShareContext({
      hasTeam: context.hasTeam,
      teamId: context.teamId,
      shareCode: context.shareCode
    });
  },
  { name: 'miniprogram-share-context' }
);

// 队伍事实自动补齐：已登录（owner 就绪）但尚未获得队伍事实时（如仅停留 MAIN/GUEST/RULES 页，
// 未经过任何登记页面），主动拉一次自己的 team_config 登记进快照——保证这些页面转发时同样
// 直接分享当前账号人的客态链接。同一 owner 只尝试一次（失败静默保持 null，分享按主入口兜底）。
const teamFactAutoFill = {
  pending: false,
  attemptedOwner: null,
  lastOwner: null
};

reaction(
  () => ({
    owner: activitySessionStore.contextOwnerAccount,
    needsFill: !!activitySessionStore.contextOwnerAccount && teamFactStore.hasTeam === null
  }),
  ({ owner, needsFill }) => {
    // 账号切换失效（PRD 01 边界-1-6）：owner 变化即清空上一账号的队伍事实并重置补齐标记，
    // 防止旧账号 hasTeam 残留串入新会话的分享与底栏组队域导航判断；清空使 hasTeam 回到 null，
    // reaction 依赖变化后按新 owner 走下方补齐重建
    if (owner !== teamFactAutoFill.lastOwner) {
      teamFactStore.invalidate();
      teamFactAutoFill.attemptedOwner = null;
      teamFactAutoFill.lastOwner = owner;
    }
    if (!needsFill || teamFactAutoFill.pending || teamFactAutoFill.attemptedOwner === owner) {
      return;
    }

    teamFactAutoFill.pending = true;
    teamFactAutoFill.attemptedOwner = owner;
    Promise.resolve()
      .then(() => loadTeamContext())
      .then(viewModel => {
        // owner 校验：补齐飞行期间账号已切换（且失效已执行）时，过期响应不得写入新账号快照
        if (teamFactAutoFill.lastOwner === owner && activitySessionStore.contextOwnerAccount === owner) {
          teamFactStore.update({ hasTeam: viewModel.hasTeam, teamId: viewModel.teamId });
        }
      })
      .catch(() => {})
      .then(() => {
        teamFactAutoFill.pending = false;
      });
  },
  { name: 'miniprogram-team-fact-auto-fill' }
);

// noinspection SpellCheckingInspection
const rootStore = {
  globalStore,
  thirdPartyAuthStore,
  xoyoAuthStore,
  activitySessionStore,
  teamFactStore
};

window.rootStore = rootStore;

export default rootStore;
